import { Tooltip } from 'flowbite-react';
import { isRecruiterSide } from 'src/config/env.config';
import { UserSkillRatingDocument } from 'types';
import MuiTooltip from '@mui/material/Tooltip';
import { useThemeContext } from 'src/context/ThemeProvider';

interface Props {
	skills: UserSkillRatingDocument[];
}

interface ToolTipContentProps {
	title: string;
	description: string;
}

const AssessedSkills = ({ skills }: Props) => {
	const { themeMode } = useThemeContext();
	const assessedSkills: UserSkillRatingDocument[] = [];
	const seenSkillNames = new Set();

	skills?.forEach(s => {
		if (!s.skill.isSoftSkill && !seenSkillNames.has(s.skill.name)) {
			if (s?.rating > 0) {
				assessedSkills.push(s);
				seenSkillNames.add(s.skill.name);
			}
		}
	});
	
	if (!assessedSkills || !assessedSkills.length) {
		return <p className="text-[13px] text-[#8b8e94]">You don’t have any assessed skills yet.</p>;
	}

	return assessedSkills?.length > 0 ? (
		<>
			{assessedSkills.map(s => {
				return (
					<div
						style={
							themeMode === 'dark'
								? {
										background:
											'linear-gradient(#1f2a37, #1f2a37) padding-box, linear-gradient(to right, #5b8def, #0063f7, #73dfe7, #0063f7) border-box',
										border: '0.5px solid transparent',
								  }
								: {
										background:
											'linear-gradient(#ffff, #ffff) padding-box, linear-gradient(to right, #5b8def, #0063f7, #73dfe7, #0063f7) border-box',
										border: '0.5px solid transparent',
								  }
						}
						key={s.skill.name}
						className="rounded-[6px] border flex gap-1 items-center justify-center px-[12px] py-[2px] text-sm font-medium dark:text-[#E4E4EB]"
					>
						{!s.skill.description && (
							<>{s.skill.name && s.skill.name.length > 20 ? `${s.skill.name.substring(0, 16)}...` : s.skill.name}</>
						)}

						{s.skill.description && s.skill.description?.length > 0 && s.skill.name.length > 0 && (
							<>
								{isRecruiterSide ? (
									<MuiTooltip
										arrow={false}
										title={<MuiTooltipcontent title={s.skill.name} description={s.skill.description || ''} />}
									>
										<span className="w-max">
											{s.skill.name && s.skill.name.length > 20
												? `${s.skill.name.substring(0, 16)?.trim()}...`
												: s.skill.name}
										</span>
									</MuiTooltip>
								) : (
									<Tooltip
										arrow={false}
										content={<Tooltipcontent title={s.skill.name} description={s.skill.description || ''} />}
										className={`!overflow-hidden !rounded-[5px] bg-white dark:bg-[#1F2A37] !p-0 text-gray-800 dark:text-[#8b8e94] sm:right-0 sm:!max-w-[255px] `}
									>
										{s.skill.name && s.skill.name.length > 20
											? `${s.skill.name.substring(0, 16)?.trim()}...`
											: s.skill.name}
									</Tooltip>
								)}
							</>
						)}

						<div className="flex items-center justify-center">
							{isRecruiterSide ? (
								<MuiTooltip
									arrow={false}
									title={
										<MuiTooltipcontent
											title="Assessed Skill Badge"
											description="This badge signifies expertise in a specific area, validated through rigorous assessment, serving as a trusted indicator of proficiency."
										/>
									}
								>
									<div className="flex items-center justify-center">
										<img
											className="ml-[4px] inline-block"
											loading="lazy"
											src={'/assets/checkCircle.svg'}
											alt="checkCircle"
											width="14"
											height="14"
										/>
									</div>
								</MuiTooltip>
							) : (
								<Tooltip
									arrow={false}
									content={
										<Tooltipcontent
											title="Assessed Skill Badge"
											description="This badge signifies expertise in a specific area, validated through rigorous assessment, serving as a trusted indicator of proficiency."
										/>
									}
									className={`!overflow-hidden !rounded-[5px] bg-white dark:bg-[#1F2A37] !p-0 text-gray-800 dark:text-[#8b8e94] sm:right-0 sm:!max-w-[255px] `}
								>
									<div className="flex items-center justify-center">
										<img
											className="ml-[4px] inline-block"
											loading="lazy"
											src={'/assets/checkCircle.svg'}
											alt="checkCircle"
											width="14"
											height="14"
										/>
									</div>
								</Tooltip>
							)}
						</div>
					</div>
				);
			})}
		</>
	) : null;
};

export const Tooltipcontent = ({ title, description }: ToolTipContentProps) => {
	return (
		<div className="!m-0 w-[255px] border rounded-lg overflow-hidden">
			<div className="dark:bg-[#374151] px-[12px] py-[6px]">
				<p className="font-inter font-[600] dark:text-white">{title}</p>
			</div>
			<div className="px-[12px] py-[8px] font-inter text-inherit">
				<p>{description}</p>
			</div>
		</div>
	);
};

export const MuiTooltipcontent = ({ title, description }: ToolTipContentProps) => {
	return (
		<div className="!m-0 w-[255px] py-1">
			<div className="">
				<p className="font-inter text-[13px] font-[600] dark:text-white">{title}</p>
			</div>
			<div className="font-inter mt-1">
				<p>{description}</p>
			</div>
		</div>
	);
};

export default AssessedSkills;
